import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import {
  FiBarChart2 as BarChart3,
  FiCalendar as Calendar,
} from "react-icons/fi";
import { Button, Card, Col, Container, Nav, Row } from "react-bootstrap";
import { store } from "../../../Store/store";
import {
  getProductionLineId,
  getLastWeekStartDate,
  getLastWeekEndDate,
} from "../../../General/general";
import { conversionUnitFactors, unitMeasurementSystem } from "../../../enums";
import { CipAnalyticsService } from "./CipAnalyticsService";
import {
  formatDuration,
  safeMath,
  safeNumber,
} from "../../../General/timeFormatter";
import CipOverviewPage from "./CipOverviewPage";
import CipAnalysisPage from "./CipAnalysisPage";
import moment from "moment";
import { ALERT_TYPE_MAPPING } from "../../../General/alertTypes";
import BeveragesDateRangePicker from "../BeveragesDateRangePicker";

export default function CipAnalyticsPage() {
  const globalState = useContext(store);
  const selectedProductionLineId = getProductionLineId(globalState);
  const unit = globalState.state.unit;

  const cipAnalyticsService = useMemo(() => {
    return new CipAnalyticsService(process.env.REACT_APP_API_URL);
  }, []);

  // State management
  const [cipData, setCipData] = useState([]);
  const [cipTotalsData, setCipTotalsData] = useState([]);
  const [alertTypes, setAlertTypes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("overview");

  // Date range state for DateRangePicker
  const [startDate, setStartDate] = useState(() => {
    const savedStartDate = sessionStorage.getItem("beveragesHomePageStartDate");
    return savedStartDate !== null
      ? new Date(JSON.parse(savedStartDate))
      : getLastWeekStartDate();
  });
  const [endDate, setEndDate] = useState(() => {
    const savedEndDate = sessionStorage.getItem("beveragesHomePageEndDate");
    return savedEndDate !== null
      ? new Date(JSON.parse(savedEndDate))
      : getLastWeekEndDate();
  });

  const [dateApplied, setDateApplied] = useState(false);

  // Memoized unit conversion functions
  const convertVolume = useCallback(
    (liters) => {
      const safeLiters = safeNumber(liters, 0);
      if (unit === unitMeasurementSystem.IMPERIAL) {
        return safeMath(
          safeLiters * conversionUnitFactors.LITERS_TO_GALLONS,
          "round"
        );
      }
      return safeMath(safeLiters, "round");
    },
    [unit]
  );

  const convertTime = useCallback((seconds) => formatDuration(seconds), []);
  const getTimeUnit = useCallback(() => "", []);

  const getVolumeUnit = useCallback(() => {
    if (unit === unitMeasurementSystem.IMPERIAL) return "Gallons";
    return "Liter";
  }, [unit]);

  // Determine if using cubic meters for formatting
  const isCubicMeters = false;

  // Memoized alert types to avoid recalculation
  const memoizedAlertTypes = useMemo(() => {
    return Object.entries(ALERT_TYPE_MAPPING).map(([id, name]) => ({
      id: parseInt(id),
      name: name,
    }));
  }, []);

  // Update loadData function
  const loadData = useCallback(async () => {
    if (!selectedProductionLineId) {
      setError("No production line selected");
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const calcStartDate = moment(startDate).utcOffset(0, true);
      const calcEndDate = moment(endDate).utcOffset(0, true);

      // Fetch both datasets in parallel
      const [cipDataResponse, totalsDataResponse] = await Promise.all([
        cipAnalyticsService.getCipAnalyticsData(
          calcStartDate.toISOString(),
          calcEndDate.toISOString(),
          selectedProductionLineId
        ),
        cipAnalyticsService.getCipAnalyticsTotals(
          calcStartDate.toISOString(),
          calcEndDate.toISOString(),
          selectedProductionLineId
        ),
      ]);

      setCipData(cipDataResponse.data || []);
      setCipTotalsData(totalsDataResponse || []);
      setAlertTypes(memoizedAlertTypes);
    } catch (error) {
      setError("Failed to load CIP analytics data. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }, [
    selectedProductionLineId,
    startDate,
    endDate,
    cipAnalyticsService,
    memoizedAlertTypes,
  ]);

  useEffect(() => {
    loadData();
  }, [loadData, dateApplied]);

  // Process data with proper grouping by cip_object_id - memoized for performance
  const processedData = useMemo(() => {
    // First, collect all alerts by CIP object
    const groupedByCipId = {};

    cipData.forEach((row) => {
      const cipId = row.cip_object_id;
      const alertStr = String(row.alert_types_csv || "").trim();

      if (!alertStr) return;

      // Initialize CIP object group if it doesn't exist
      if (!groupedByCipId[cipId]) {
        groupedByCipId[cipId] = {
          ...row, // Take first row's metadata (object_name, recipe_name, etc.)
          step_start_date: new Date(row.cip_start_time), // Use CIP start time, not step start time
          allAlerts: new Set(),
          totalTimeWaste: 0,
          totalWaterWaste: 0,
          totalChemicalWaste: 0,
        };
      }

      // Add alerts from this step group
      if (alertStr.includes(",")) {
        const alertIds = alertStr.split(",").map((id) => id.trim());
        alertIds.forEach((alertId) =>
          groupedByCipId[cipId].allAlerts.add(alertId)
        );
      } else {
        groupedByCipId[cipId].allAlerts.add(alertStr);
      }

      // Accumulate waste values
      groupedByCipId[cipId].totalTimeWaste += safeNumber(row.time_waste_s, 0);
      groupedByCipId[cipId].totalWaterWaste += safeNumber(row.water_waste_l, 0);
      groupedByCipId[cipId].totalChemicalWaste += safeNumber(
        row.chemical_waste_l,
        0
      );
    });

    // Convert to final format - one row per CIP object per alert
    const result = [];
    Object.values(groupedByCipId).forEach((cipObj) => {
      const allAlerts = Array.from(cipObj.allAlerts);

      allAlerts.forEach((alertId, alertIndex) => {
        result.push({
          ...cipObj,
          alert_id: alertId,
          alert_types_csv: allAlerts.join(","), // All alerts for this CIP object
          time_waste_s: cipObj.totalTimeWaste / allAlerts.length,
          water_waste_l: cipObj.totalWaterWaste / allAlerts.length,
          chemical_waste_l: cipObj.totalChemicalWaste / allAlerts.length,
        });
      });
    });

    return result;
  }, [cipData]);

  const filteredData = processedData;

  // Calculate summary metrics
  const summaryMetrics = useMemo(() => {
    const totalAlerts = filteredData.length;
    const totalTimeWaste = filteredData.reduce(
      (sum, row) => sum + safeNumber(row.time_waste_s, 0),
      0
    );
    const totalWaterWaste = filteredData.reduce(
      (sum, row) => sum + safeNumber(row.water_waste_l, 0),
      0
    );
    const totalChemicalWaste = filteredData.reduce(
      (sum, row) => sum + safeNumber(row.chemical_waste_l, 0),
      0
    );

    return { totalAlerts, totalTimeWaste, totalWaterWaste, totalChemicalWaste };
  }, [filteredData]);

  if (isLoading) {
    return (
      <div style={{ backgroundColor: "#f5f7fb", minHeight: "100vh" }}>
        <div
          className="d-flex align-items-center justify-content-center"
          style={{ height: "50vh" }}
        >
          <div className="text-center">
            <div className="spinner-border text-primary mb-3" role="status">
              <span className="sr-only">Loading...</span>
            </div>
            <p className="text-muted">Loading CIP Analytics...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ backgroundColor: "#f5f7fb", minHeight: "100vh" }}>
        <div
          className="d-flex align-items-center justify-content-center"
          style={{ height: "50vh" }}
        >
          <div className="text-center">
            <div className="alert alert-danger" role="alert">
              <h6 className="alert-heading">Error Loading Data</h6>
              <p className="mb-0">{error}</p>
            </div>
            <Button variant="primary" onClick={() => window.location.reload()}>
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{ backgroundColor: "#f5f7fb", minHeight: "100vh" }}
      className="pb-4 mb-4"
    >
      <Card bsPrefix="main-card-style">
        <Container fluid>
          {/* Header - Removed Unit System Toggle */}
          <Row className="pt-4 pb-3">
            <Col>
              <div className="d-flex align-items-center justify-content-between mb-3">
                <div className="d-flex align-items-center">
                  <BarChart3 size={32} className="text-primary mr-3" />
                  <div>
                    <h2 className="mb-1 font-weight-bold">Wint CIP Analyzer</h2>
                    <small className="text-muted">Units: {unit}</small>
                  </div>
                </div>
              </div>

              {/* Tab Navigation */}
              <Nav variant="tabs" activeKey={activeTab} onSelect={setActiveTab}>
                <Nav.Item>
                  <Nav.Link eventKey="overview">Overview</Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="analysis">Analysis</Nav.Link>
                </Nav.Item>
              </Nav>
            </Col>
          </Row>

          {/* Date Range Selector using your DateRangePicker component */}
          <Row className="pb-4">
            <Col lg={3}>
              <Card className="border-0 bg-light">
                <Card.Body className="p-3">
                  <div className="d-flex align-items-center mb-3">
                    <Calendar size={20} className="text-muted mr-2" />
                    <h6 className="mb-0 font-weight-medium">
                      Select Date Range
                    </h6>
                  </div>

                  <BeveragesDateRangePicker
                    title="CIP Analytics"
                    startDate={startDate}
                    endDate={endDate}
                    setStartDate={setStartDate}
                    setEndDate={setEndDate}
                    setDateApplied={setDateApplied}
                    hasExportCsv={false}
                    fontSize="14px"
                  />
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Tab Content */}
          {activeTab === "overview" && (
            <CipOverviewPage
              filteredData={filteredData}
              totalAlerts={summaryMetrics.totalAlerts}
              totalTimeWaste={summaryMetrics.totalTimeWaste}
              totalWaterWaste={summaryMetrics.totalWaterWaste}
              totalChemicalWaste={summaryMetrics.totalChemicalWaste}
              cipTotalsData={cipTotalsData}
              convertVolume={convertVolume}
              convertTime={convertTime}
              getVolumeUnit={getVolumeUnit}
              getTimeUnit={getTimeUnit}
              isCubicMeters={isCubicMeters}
              alertTypes={alertTypes}
            />
          )}

          {activeTab === "analysis" && (
            <CipAnalysisPage
              filteredData={filteredData}
              // processedTotals={processedTotals}
              cipTotalsData={cipTotalsData}
              totalAlerts={summaryMetrics.totalAlerts}
              totalTimeWaste={summaryMetrics.totalTimeWaste}
              totalWaterWaste={summaryMetrics.totalWaterWaste}
              totalChemicalWaste={summaryMetrics.totalChemicalWaste}
              convertVolume={convertVolume}
              convertTime={convertTime}
              getVolumeUnit={getVolumeUnit}
              getTimeUnit={getTimeUnit}
              isCubicMeters={isCubicMeters}
              alertTypes={alertTypes}
            />
          )}
        </Container>
      </Card>
    </div>
  );
}
